import { HttpErrorResponse } from '@angular/common/http';

import { createSelectorFactory, defaultMemoize, MemoizedSelector } from '@ngrx/store';

import { JobPlacement, Placement, Position } from '@recruitee/http-types';
import { LocationSelectors } from '@recruitee/locations-data';
import {
  createEntityCollectionStore,
  EntityState,
  getEntityCollectionActions,
  memoizeSelector,
  pipeState,
} from '@recruitee/ngrx';
import { JobsBriefSelectors, StageSelectors } from '@recruitee/offers-data';
import { TalentPoolsNewBriefSelectors } from '@recruitee/talent-pools-data';

import {
  PlacementChangeStageParams,
  PlacementDisqualifyParams,
  UpdateHiringDetailsParams,
} from './placement-http-client';

const idSelector = ({ id }: { id: number }) => id;
const { actions, reducer, namespace, selectors } = createEntityCollectionStore<Placement>()(
  'offer-placements',
  {
    idSelector,
    references: makeRef => [
      makeRef('offerId', 'offer', id => JobsBriefSelectors.byId(id!)),
      makeRef('talentPoolId', 'talentPool', id => TalentPoolsNewBriefSelectors.byId(id!)),
      makeRef('stageId', 'stage', StageSelectors.byId),
      makeRef('locationIds', 'locations', LocationSelectors.byIds),
    ],
    // eslint-disable-next-line max-lines-per-function
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      const updatePositions = (state: EntityState<Placement>, payload: { positions: Position[] }) =>
        adapter.markAsPending(state, {
          scope: null,
          entities: payload.positions.map(({ id }) => id),
        });

      const updatePositionsSuccess = (
        state: EntityState<Placement>,
        payload: { placements: Placement[] },
      ) =>
        pipeState(
          state,
          next => adapter.setMany(next, payload.placements),
          next =>
            adapter.markAsLoaded(next, {
              scope: null,
              entities: payload.placements.map(({ id }) => id),
            }),
        );

      const updatePositionsFailed = (
        state: EntityState<Placement>,
        payload: { error: HttpErrorResponse; positions: Position[] },
      ) =>
        adapter.setError(state, payload.error, {
          scope: null,
          entities: payload.positions.map(({ id }) => id),
        });

      return {
        createOnePessimistic: (
          state,
          {
            candidateId,
            correlationId,
            offerId,
            talentPoolId,
            locationIds,
          }: {
            candidateId: number;
            offerId?: number;
            talentPoolId?: number;
            locationIds?: number[] | null;
            correlationId: string;
          },
        ) =>
          defaultActions.createOnePessimistic(state, {
            data: { candidateId, offerId, talentPoolId, locationIds },
            correlationId,
          }),

        updatePositions,
        updatePositionsSuccess,
        updatePositionsFailed,

        updateCandidatePositions: updatePositions,
        updateCandidatePositionsSuccess: updatePositionsSuccess,
        updateCandidatePositionsFailed: updatePositionsFailed,

        requalify: (state, payload: { id: number }) => state,
        requalifySuccess: (state, payload: { placement: Placement }) =>
          pipeState(state, next => adapter.setOne(next, payload.placement)),
        requalifyFailed: (state, payload: { error: HttpErrorResponse; id: number }) => state,

        disqualify: (
          state,
          payload: { params: PlacementDisqualifyParams; placement: JobPlacement },
        ) => state,
        disqualifySuccess: (state, payload: { placement: Placement }) =>
          pipeState(state, next => adapter.setOne(next, payload.placement)),
        disqualifyCancel: (state, payload: { placement: Placement }) => state,
        disqualifyFailed: (
          state,
          payload: { error: HttpErrorResponse; params: PlacementDisqualifyParams },
        ) => state,
        changePreferredWorkLocations: (
          state,
          payload: { placementId: number; locationIds: number[] },
        ) => state,
        changePreferredWorkLocationsSuccess: (state, payload: { placement: Placement }) =>
          pipeState(state, next => adapter.setOne(next, payload.placement)),
        changePreferredWorkLocationsFailed: (state, payload: { error: HttpErrorResponse }) => state,

        changeStage: (
          state,
          payload: { params: PlacementChangeStageParams; changedHireStatus?: boolean },
        ) => state,
        changeStageSuccess: (
          state,
          payload: { placement: Placement; changedHireStatus?: boolean },
        ) => state,
        changeStageFailed: (
          state,
          payload: { error: HttpErrorResponse; params: PlacementChangeStageParams },
        ) => state,

        fetchHiringDetails: (state, payload: { placementId: number }) => state,
        fetchHiringDetailsSuccess: (state, payload: { placement: JobPlacement }) =>
          adapter.upsertOne(state, payload.placement),
        fetchHiringDetailsFailed: (
          state,
          payload: { error: HttpErrorResponse; placementId: number },
        ) => state,

        updateHiringDetails: (state, payload: { params: UpdateHiringDetailsParams }) => state,
        updateHiringDetailsSuccess: (state, payload: { placement: Placement }) =>
          adapter.upsertOne(state, payload.placement),
        updateHiringDetailsFailed: (
          state,
          payload: { error: HttpErrorResponse; params: UpdateHiringDetailsParams },
        ) => state,

        references: (state, payload: { data: Placement[] }) =>
          adapter.writeMany(state, payload.data),
      };
    },
  },
);

const PlacementActions: Pick<
  typeof actions,
  | 'createOnePessimistic'
  | 'createOnePessimisticSuccess'
  | 'createOnePessimisticFailed'
  | 'removeOnePessimistic'
  | 'removeOnePessimisticSuccess'
  | 'removeOnePessimisticFailed'
  | 'updatePositions'
  | 'updatePositionsSuccess'
  | 'updatePositionsFailed'
  | 'updateCandidatePositions'
  | 'updateCandidatePositionsSuccess'
  | 'updateCandidatePositionsFailed'
  | 'requalify'
  | 'requalifySuccess'
  | 'requalifyFailed'
  | 'disqualify'
  | 'disqualifySuccess'
  | 'disqualifyCancel'
  | 'disqualifyFailed'
  | 'changeStage'
  | 'changeStageSuccess'
  | 'changeStageFailed'
  | 'updateHiringDetailsSuccess'
  | 'updateHiringDetailsFailed'
  | 'updateHiringDetails'
  | 'fetchHiringDetailsFailed'
  | 'fetchHiringDetailsSuccess'
  | 'fetchHiringDetails'
  | 'changePreferredWorkLocations'
  | 'changePreferredWorkLocationsSuccess'
  | 'changePreferredWorkLocationsFailed'
  | 'references'
> = actions;

const byIdsWithReferences: (placementIds: number[]) => MemoizedSelector<any, Placement[]> =
  memoizeSelector(
    (placementIds: number[]): MemoizedSelector<any, Placement[]> =>
      createSelectorFactory(defaultMemoize)(
        ...placementIds.map(placementId => selectors.byIdWithReferences(placementId)!),
        (...result: Placement[]) => result.filter(Boolean),
      ),
  );

const PlacementSelectors = {
  ...selectors,
  byIdsWithReferences,
};

export {
  PlacementActions,
  PlacementSelectors,
  reducer as PlacementReducer,
  namespace as PlacementNamespace,
};
